// account-setup.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { NgxTippyInstance } from 'ngx-tippy-wrapper';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { PortalSocketService } from 'src/app/shared/services/portal-socket.service';
import {
  loadAccountSetupStepsActions,
  stepUpdate,
} from 'src/app/shared/store/_features/account-setup/account-setup.actions';
import {
  selectAccountSetupNextDoStep,
  selectAccountSetupSteps,
} from 'src/app/shared/store/_features/account-setup/account-setup.selector';
import { colorSet } from 'src/app/models/global-classes';
import {
  AccountSetupStep,
  AccountSetupStepStatusEnum,
} from 'src/app/models/owner-dashboard';

interface StepWeights {
  create_hub: number;
  publish_first_course: number;
  create_blog_post: number;
  add_social_links: number;
}

@Injectable({
  providedIn: 'root',
})
export class AccountSetupService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly stepDoneSound = new Audio('assets/notification.mp3');

  // Step weights for progress calculation
  private readonly STEP_WEIGHTS: StepWeights = {
    create_hub: 40,
    publish_first_course: 20,
    create_blog_post: 20,
    add_social_links: 20,
  };

  // UI State
  sideWidgetColor: colorSet = 'primary';
  tippyMessage = 'Get Help';
  tippyInstance: NgxTippyInstance;

  // Setup State
  completionPercentage = 0;
  steps: AccountSetupStep[] = [];
  nextStep: AccountSetupStep | null = null;
  isStepsDone = false;

  constructor(
    private store: Store,
    private socketService: PortalSocketService
  ) {
    this.initializeStore();
    this.initializeSocketListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize store subscriptions
   */
  private initializeStore(): void {
    this.loadSteps();

    this.store
      .select(selectAccountSetupSteps)
      .pipe(
        filter((steps) => !!steps),
        takeUntil(this.destroy$)
      )
      .subscribe((steps) => {
        this.steps = steps;
        this.calculateProgress();
      });

    this.store
      .select(selectAccountSetupNextDoStep)
      .pipe(takeUntil(this.destroy$))
      .subscribe((nextStep) => {
        this.nextStep = nextStep;
      });
  }

  /**
   * Manually trigger step reload
   */
  public loadSteps(): void {
    this.store.dispatch(loadAccountSetupStepsActions.load());
  }

  /**
   * Initialize socket listener
   */
  private initializeSocketListener(): void {
    this.socketService.socketReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.startProgressListening();
      });
  }

  /**
   * Start listening for progress updates
   */
  private startProgressListening(): void {
    this.socketService.accountSetup$
      .pipe(
        filter((step) => !!step),
        takeUntil(this.destroy$)
      )
      .subscribe((step: AccountSetupStep) => {
        this.handleStepUpdate(step);
      });
  }

  /**
   * Handle step update from socket
   */
  private handleStepUpdate(updatedStep: AccountSetupStep): void {
    const previousStep = this.steps.find((s) => s.id === updatedStep.id);

    if (!previousStep) {
      return;
    }

    this.store.dispatch(stepUpdate({ step: updatedStep }));

    // Check if step was undone
    if (
      previousStep.status === AccountSetupStepStatusEnum.DONE &&
      updatedStep.status === AccountSetupStepStatusEnum.NONE
    ) {
      this.handleStepUndone();
    } else if (updatedStep.status === AccountSetupStepStatusEnum.DONE) {
      this.handleStepCompleted(updatedStep);
    }
  }

  /**
   * Handle step completion
   */
  private handleStepCompleted(step: AccountSetupStep): void {
    this.playStepDoneSound();
    this.showMessage(
      'You have completed a step. Feel free to reach out if you need assistance.',
      0,
      5000,
      'success'
    );
  }

  /**
   * Handle step being undone
   */
  private handleStepUndone(): void {
    this.showMessage(
      "You've stepped back; feel free to reach out if assistance is required.",
      0,
      4000,
      'danger'
    );
  }

  /**
   * Calculate completion percentage
   */
  private calculateProgress(): void {
    if (!this.steps?.length) {
      this.completionPercentage = 0;
      return;
    }

    let totalProgress = 0;

    this.steps.forEach((step) => {
      if (step.status === AccountSetupStepStatusEnum.DONE) {
        const weight = this.STEP_WEIGHTS[step.actionEnum as keyof StepWeights] || 0;
        totalProgress += weight;
      }
    });

    this.completionPercentage = Math.round(totalProgress);
  }

  /**
   * Show tippy message
   */
  showMessage(
    content: string,
    openTimeout = 0,
    closeTimeout = 1500,
    color: colorSet = 'primary'
  ): void {
    this.tippyMessage = content;

    setTimeout(() => {
      if (!this.tippyInstance) {
        return;
      }

      this.tippyInstance.show();
      this.sideWidgetColor = color;

      if (closeTimeout) {
        setTimeout(() => {
          this.tippyInstance?.hide();
          this.sideWidgetColor = 'primary';
        }, closeTimeout);
      }
    }, openTimeout);
  }

  /**
   * Play step completion sound
   */
  private playStepDoneSound(): void {
    this.stepDoneSound.play().catch((error) => {
      console.warn('Could not play step done sound:', error);
    });
  }

  /**
   * Get step by action enum
   */
  getStepByAction(actionEnum: string): AccountSetupStep | undefined {
    return this.steps.find((step) => step.actionEnum === actionEnum);
  }

  /**
   * Check if all steps are completed
   */
  get allStepsCompleted(): boolean {
    return this.steps.every((step) => step.status === AccountSetupStepStatusEnum.DONE);
  }

  /**
   * Get completed steps count
   */
  get completedStepsCount(): number {
    return this.steps.filter((step) => step.status === AccountSetupStepStatusEnum.DONE).length;
  }

  /**
   * Get total steps count
   */
  get totalStepsCount(): number {
    return this.steps.length;
  }
}